import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Unlink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR, fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label, Loader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function Connect({ onDone }) {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const go = (e) => { e.preventDefault(); setBusy(true); api.put("/quantelys/config", { email, password: pwd }).then(() => { toast.success("Quantelys connecté"); onDone(); }).catch((er) => toast.error(apiErr(er))).finally(() => setBusy(false)); };
  return (
    <form onSubmit={go} className={`${CARD} max-w-lg space-y-4 p-6`} data-testid="quantelys-connect">
      <Label>Connecter Quantelys</Label>
      <p className="text-sm text-gray-500">Lecture seule : ce logiciel lit vos produits, quantités et prix, sans jamais rien modifier dans Quantelys. Conseil : créez dans Quantelys un utilisateur au rôle « viewer » réservé à cette liaison.</p>
      <Input type="email" required placeholder="E-mail du compte Quantelys" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quantelys-email" />
      <Input type="password" required placeholder="Mot de passe Quantelys" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="quantelys-password" />
      <Button type="submit" disabled={busy} className="rounded-full bg-[#065F46] hover:bg-[#054d39]" data-testid="quantelys-connect-btn"><Link2 className="mr-1.5 h-4 w-4" />{busy ? "Connexion…" : "Connecter"}</Button>
    </form>
  );
}

function StockTable({ data }) {
  return (
    <div className={`${CARD} overflow-x-auto`} data-testid="quantelys-products">
      <table className="w-full min-w-[620px] text-sm">
        <thead><tr className="border-b border-gray-100 text-left text-xs text-gray-400"><th className="px-4 py-2.5">Produit</th><th>Fournisseur</th><th className="text-right">Stock</th><th className="text-right">Prix unit.</th><th className="pr-4 text-right">Valeur</th></tr></thead>
        <tbody className="divide-y divide-gray-50">{data.products.map((p) => (
          <tr key={p.id}><td className="px-4 py-2"><p className="font-medium">{p.name}</p><p className="text-xs text-gray-400">{p.category}{p.expired ? " · périmé" : ""}</p></td><td className="text-gray-500">{p.supplier}</td>
            <td className={`text-right tabular-nums ${p.low ? "font-semibold text-amber-600" : ""}`}>{p.stock} {p.unit || ""}{p.low ? " ⚑" : ""}</td><td className="text-right tabular-nums">{fmtEUR2(p.unit_price)}</td><td className="pr-4 text-right tabular-nums">{fmtEUR(p.value)}</td></tr>))}</tbody>
      </table>
    </div>
  );
}

function Matches() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/quantelys/matches").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loader />;
  return (
    <div className={`${CARD} divide-y divide-gray-100`} data-testid="quantelys-matches">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
          <div className="min-w-0"><p className="truncate font-medium">{r.designation}</p><p className="text-xs text-gray-400">{r.supplier} · {fmtDate(r.invoice_date)} · {fmtEUR2(r.unit_price_ht)} HT</p></div>
          {r.product ? <div className="text-right"><p className="text-[#065F46]">→ {r.product.name}</p><p className="text-xs text-gray-400">stock {r.product.stock} · prix Quantelys {fmtEUR2(r.product.unit_price)}{r.price_gap_pct != null && ` · écart ${r.price_gap_pct > 0 ? "+" : ""}${r.price_gap_pct} %`}</p></div>
            : <span className="text-xs text-gray-400">Aucun produit correspondant</span>}
        </div>))}
      {!rows.length && <p className="p-6 text-center text-sm text-gray-400">Aucune ligne d'achat (comptes 60…) sur les 120 derniers jours.</p>}
    </div>
  );
}

export function Stock() {
  const [conf, setConf] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr(""); setData(null);
    api.get("/quantelys/config").then((r) => { setConf(r.data); if (r.data.connected) { api.get("/quantelys/stock").then((s) => setData(s.data)).catch((e) => setErr(apiErr(e))); } });
  }, []);
  useEffect(() => { load(); }, [load]);
  const disconnect = () => api.delete("/quantelys/config").then(() => { toast.success("Quantelys déconnecté"); load(); });
  if (!conf) return <Loader />;
  if (!conf.connected) return <Connect onDone={load} />;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500" data-testid="quantelys-status">Connecté avec <b>{conf.email}</b>{conf.last_sync ? ` · lu le ${fmtDate(conf.last_sync)}` : ""} · lecture seule</p>
        <Button variant="outline" size="sm" onClick={disconnect} data-testid="quantelys-disconnect"><Unlink className="mr-1.5 h-4 w-4" />Déconnecter</Button>
      </div>
      {err && <p className="rounded-2xl bg-rose-50 p-4 text-sm text-rose-700" data-testid="quantelys-error">{err}</p>}
      {!data && !err && <Loader label="Lecture de Quantelys…" />}
      {data && <>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[["Valeur du stock", fmtEUR(data.summary.value)], ["Produits", data.summary.count], ["Sous le seuil", data.summary.low], ["Périmés", data.summary.expired]].map(([l, v]) => (
          <div key={l} className={`${CARD} p-5`}><p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{l}</p><p className="mt-1 font-display text-2xl font-bold tabular-nums">{v}</p></div>))}</div>
        <StockTable data={data} />
        <div><Label className="mb-2">Lignes de facture rapprochées des produits Quantelys</Label><Matches /></div>
      </>}
    </div>
  );
}
